import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import ComboFiltro from '../../../components/ComboFiltro';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { API_DOCS } from './DistintePage.api';
import './DistintePage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDistinta = 'MERCE' | 'LAVORAZIONI';
type AddebitoDistinta = 'NOSTRO' | 'CLIENTE';
type StatoDistinta = 'BOZZA' | 'CONFERMATA';

interface Distinta {
  id: number;
  commessaId: number | null;
  ordineId: number;
  numero: number;
  tipo: TipoDistinta;
  addebito: AddebitoDistinta;
  descrizione: string | null;
  stato: StatoDistinta;
  confermataAt: string | null;
  createdAt: string;
  ordine: {
    id: number;
    numero: string;
    stato: string;
    flussoCommerciale: string;
    societaId: number;
  };
  commessa: {
    id: number;
    numero: string;
    descrizione: string | null;
    clienteRagioneSociale: string | null;
  } | null;
  confermataDa: { id: number; nome: string; cognome?: string | null } | null;
  _count: { righe: number };
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function etichettaDistinta(d: Distinta): string {
  return `${d.ordine.numero}-D${d.numero}`;
}

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function renderTipo(tipo: TipoDistinta): React.ReactNode {
  return tipo === 'MERCE'
    ? <span className="bop-badge bop-badge-info">Merce</span>
    : <span className="bop-badge dist-badge-lavorazioni">Lavorazioni</span>;
}

function renderAddebito(addebito: AddebitoDistinta): React.ReactNode {
  return addebito === 'NOSTRO'
    ? <span className="bop-badge bop-badge-neutral">Nostro carico</span>
    : <span className="bop-badge bop-badge-warning">Carico cliente</span>;
}

function renderStato(stato: StatoDistinta): React.ReactNode {
  return stato === 'BOZZA'
    ? <span className="bop-badge bop-badge-neutral">Bozza</span>
    : <span className="bop-badge bop-badge-success">Confermata</span>;
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function DistintePage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [tipo, setTipo] = useState('');
  const [stato, setStato] = useState('');
  const [commessaId, setCommessaId] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [items, setItems] = useState<Distinta[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);

  // Debounce ricerca testo (client-side)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (tipo) params.set('tipo', tipo);
      if (stato) params.set('stato', stato);
      if (commessaId) params.set('commessaId', commessaId);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/distinte?societaId&tipo&stato&commessaId -> {"success":true,"data":[{"id":"number","commessaId":"number","ordineId":"number","numero":"number","tipo":"MERCE|LAVORAZIONI","addebito":"NOSTRO|CLIENTE","descrizione":"string","stato":"BOZZA|CONFERMATA","confermataAt":"string","createdAt":"string","ordine":{"id":"number","numero":"string","stato":"string","flussoCommerciale":"string","societaId":"number"},"commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"confermataDa":{"id":"number","nome":"string","cognome":"string"},"_count":{"righe":"number"}}]}
      const r = await apiFetch<{ success: boolean; data: Distinta[] }>(`/api/distinte${qs}`);
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nel caricamento delle distinte', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, tipo, stato, commessaId]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Carica commesse per il combo (cambia con societaId effettiva)
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Resetta commessa se cambia società
  useEffect(() => { setCommessaId(''); }, [societaIdEff]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  // Ricerca testo client-side: numero ordine, commessa, cliente, descrizione
  const itemsFiltrati = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter(d =>
      [
        etichettaDistinta(d),
        d.ordine?.numero,
        d.commessa?.numero,
        d.commessa?.clienteRagioneSociale,
        d.descrizione,
      ].some(v => (v || '').toLowerCase().includes(s))
    );
  }, [items, q]);

  const pulisciFiltri = () => {
    setTipo('');
    setStato('');
    setCommessaId('');
    setQInput('');
    setQ('');
  };

  return (
    <Layout>
      <div className="dist-page">
        {/* Header — le distinte singole nascono dalla scheda ordine;
            da qui si accede al wizard di generazione massiva */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Distinte</h1>
          {can('commerciale.distinte', 'edit') && (
            <button
              className="bop-btn-primary"
              onClick={() => navigate('/commerciale/attivo/distinte/nuove')}
            >
              + Genera distinte
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar dist-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input dist-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Ordine, commessa, cliente, descrizione..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Tipo</label>
            <select className="bop-select" value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="">Tutti</option>
              <option value="MERCE">Merce</option>
              <option value="LAVORAZIONI">Lavorazioni</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select className="bop-select" value={stato} onChange={e => setStato(e.target.value)}>
              <option value="">Tutti</option>
              <option value="BOZZA">Bozza</option>
              <option value="CONFERMATA">Confermata</option>
            </select>
          </div>

          <div className="bop-form-group dist-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare le distinte</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Distinta</th>
                  <th>Tipo</th>
                  <th>Addebito</th>
                  <th>Ordine</th>
                  <th>Commessa</th>
                  <th className="bop-text-right">Righe</th>
                  <th>Stato</th>
                  <th>Confermata il</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="bop-empty-state">Caricamento…</div>
                    </td>
                  </tr>
                ) : itemsFiltrati.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="bop-empty-state">
                        <h3 className="bop-empty-state__title">Nessuna distinta trovata</h3>
                        <p className="bop-empty-state__text">
                          Genera le distinte in modo massivo con il bottone
                          "+ Genera distinte" in alto, oppure crea la singola distinta
                          dalla scheda dell'ordine di vendita
                          (sezione Distinte merce / lavorazioni).
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  itemsFiltrati.map(d => (
                    <tr key={d.id} onClick={() => navigate(`/commerciale/attivo/distinte/${d.id}`)}>
                      <td><span className="dist-etichetta">{etichettaDistinta(d)}</span></td>
                      <td>{renderTipo(d.tipo)}</td>
                      <td>{renderAddebito(d.addebito)}</td>
                      <td>{d.ordine?.numero || <span className="bop-text-muted">—</span>}</td>
                      <td>
                        {d.commessa ? (
                          <div className="dist-commessa-cell">
                            <span className="dist-commessa-numero">{d.commessa.numero}</span>
                            {d.commessa.clienteRagioneSociale && (
                              <span className="bop-text-muted">{d.commessa.clienteRagioneSociale}</span>
                            )}
                          </div>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td className="bop-text-right">{d._count?.righe ?? 0}</td>
                      <td>{renderStato(d.stato)}</td>
                      <td>
                        {d.confermataAt
                          ? formatDateIT(d.confermataAt)
                          : <span className="bop-text-muted">—</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
